import { useState, useCallback, useEffect, useMemo } from "react";
import approverMaintenanceService from "../services/approverMaintenanceService";
import { useEnumsApi } from "./useEnumsApi";
import { useGeneralMessage } from "../../common/APICommon";

export const useApproverMaintenance = () => {
  const [loading, setLoading] = useState(false);
  const [headers, setHeaders] = useState([]);
  const [maintenanceLabels, setMaintenanceLabels] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  const { approverMaintenanceTypes, fetchApproverMaintenanceTypes, fetchMaintenanceData } = useEnumsApi();

  // Messaging state inside the hook
  const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await approverMaintenanceService.getHeaders();
      setHeaders(data);

      const typeIds = [...new Set(data.map(h => h.maintenanceTypeId))];
      if (typeIds.length > 0) {
        const labels = await fetchMaintenanceData(typeIds);
        setMaintenanceLabels(labels);
      }
    } catch (err) {
      console.error("Fetch Error", err);
      showGeneralMessage("Failed to load maintenance data.", false);
    } finally {
      setLoading(false);
    }
  }, [fetchMaintenanceData, showGeneralMessage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const groupedData = useMemo(() => {
    const grouped = {};
    headers.forEach(h => {
      const typeId = h.maintenanceTypeId;
      if (!grouped[typeId]) grouped[typeId] = [];
      const typeLabels = maintenanceLabels[typeId.toString()] || [];
      const labelMatch = typeLabels.find(l => l.value === h.maintenanceValueId);
      const valueName = labelMatch ? labelMatch.label : `ID: ${h.maintenanceValueId}`;

      const searchLower = searchTerm.toLowerCase();
      if (valueName.toLowerCase().includes(searchLower) || h.priorityLevel?.toString().includes(searchLower)) {
        grouped[typeId].push({ ...h, maintenanceValueName: valueName });
      }
    });
    return grouped;
  }, [headers, maintenanceLabels, searchTerm]);

  const saveApproverMaintenance = async (payload, id) => {
    setLoading(true);
    try {
      await approverMaintenanceService.save(payload, id);
      await fetchData();
      showGeneralMessage("Configuration saved successfully!", true);
      return { success: true };
    } catch (err) {
      const errMsg = err.response?.data?.message || err.message;
      showGeneralMessage(errMsg, false);
      return { success: false, error: errMsg };
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    groupedData,
    approverMaintenanceTypes,
    searchTerm,
    setSearchTerm,
    saveApproverMaintenance,
    showMessage,
    message,
    isSuccess,
    showGeneralMessage
  };
};